import * as React from 'react';
import Typography from '@mui/material/Typography';
import { dataConstants } from '../../constants/index.js';
import { Box, Grid, IconButton, ListItemButton } from '@mui/material';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { levenshtein } from '../../utils/levenshtein/index.js';
import { useRouter } from "next/router";
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Link from 'next/link';

const { INDUSTRY: industries, SECTOR_OPTIONS } = dataConstants;
const sectors = SECTOR_OPTIONS?.sort((a, b) =>
  a.shortName.localeCompare(b.shortName, undefined, { sensitivity: 'base' })
);

const findClosestMatch = (query) => {
  const closestMatch = industries.reduce(
    (closest, item) => {
      const sectorDistance = levenshtein(query, item.sector.toLowerCase());
      const shortNameDistance = levenshtein(query, item.shortName.toLowerCase());
      if (sectorDistance < closest.distance || shortNameDistance < closest.distance) {
        return { distance: Math.min(sectorDistance, shortNameDistance), item };
      }
      return closest;
    },
    { distance: Number.MAX_SAFE_INTEGER, item: null }
  );
  return closestMatch.item;
};

const slugify = (str) =>
  str?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase();

const IndustriesList = ({ industryName, popularIndustries, subTab, sx }) => {
  const router = useRouter();
  const [filteredIndustries, setFilteredIndustries] = React.useState(industries);
  const [searchText, setSearchText] = React.useState('');
  const [suggestion, setSuggestion] = React.useState(null);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchText(query);

    if (query.length === 0) {
      setSuggestion(null);
      setFilteredIndustries(industries);
      return;
    }

    const filteredItems = industries.filter(
      (item) =>
        item.sector.toLowerCase().includes(query) ||
        item.shortName.toLowerCase().includes(query)
    );

    if (filteredItems.length === 0) {
      const closestMatch = findClosestMatch(query);
      setSuggestion(closestMatch);
    } else {
      setSuggestion(null);
    }

    setFilteredIndustries(filteredItems.length > 0 ? filteredItems : industries);
  };

  const clearSearch = () => {
    setSearchText('');
    setSuggestion(null);
    setFilteredIndustries(industries);
  };

  return (
    <Grid
      size={{ xs: 12, sm: 3.5, md: 2 }}
      sx={{
        '& .MuiTextField-root.searchField': {
          minWidth: '100% !important',
          mt: '0px !important',
          mb: '10px !important',
        },
        ...sx,
      }}
    >
      {/* Search Field */}
      <TextField
        variant="outlined"
        size="small"
        value={searchText}
        onChange={handleSearch}
        placeholder="Search sectors or ind..."
        className="searchField"
        InputProps={{
          startAdornment: (
            <SearchIcon
              fontSize="small"
              sx={{ mr: 0.5, color: 'text.disabled', fontSize: '1.1rem' }}
            />
          ),
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear search"
              size="small"
              sx={{
                visibility: searchText ? 'visible' : 'hidden',
                '& .MuiSvgIcon-root': { fontSize: '1rem' },
              }}
              onClick={clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '.85rem',
            backgroundColor: '#fff',
            px: 1,
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
              borderWidth: '1.5px',
            },
          },
          '& .MuiInputBase-input': {
            py: '8px',
            fontSize: '.85rem',
          },
          '& .MuiInputBase-input::placeholder': {
            fontSize: '.83rem',
            opacity: 0.6,
          },
        }}
      />

      {/* Fuzzy suggestion */}
      {suggestion && searchText && (
        <Box
          sx={{
            mt: 0.5,
            mb: 1,
            p: 1.5,
            borderRadius: '8px',
            backgroundColor: 'rgba(22,27,87,0.03)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '.8rem', color: 'text.secondary' }}>
            No match for &ldquo;<b>{searchText}</b>&rdquo;. Try{' '}
            <Link
              href={`/sectors/industry/${slugify(suggestion?.shortName)}`}
              style={{ color: '#174ea6', fontWeight: 600, textDecoration: 'none' }}
            >
              {suggestion?.shortName}
            </Link>
            ?
          </Typography>
        </Box>
      )}

      {/* Industry List */}
      <List
        dense
        sx={{
          width: '100%',
          position: 'relative',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 240px)',
          '& ul': { padding: 0 },
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '10px',
          backgroundColor: '#fff',
          '&::-webkit-scrollbar': { width: '4px !important' },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: '4px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.15)',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
        }}
        subheader={<li />}
      >
        {sectors?.map((sector) => {
          const sectorSlug = slugify(sector?.shortName);
          const sectorIndustries = filteredIndustries
            ?.filter((i) => i.sector === sector?.shortName)
            ?.sort((a, b) =>
              a.shortName.localeCompare(b.shortName, undefined, {
                sensitivity: 'base',
              })
            );

          if (!sectorIndustries || sectorIndustries.length === 0) return null;

          return (
            <li key={sectorSlug}>
              <ul style={{ padding: 0 }}>
                <ListSubheader
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'rgb(100,116,139)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    lineHeight: '32px',
                    px: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: '#fff',
                  }}
                >
                  {sector?.legend}
                </ListSubheader>
                {sectorIndustries.map((ind) => {
                  const industrySlug = slugify(ind?.shortName);
                  const isSelected = industryName === ind?.shortName;

                  return (
                    <ListItemButton
                      key={industrySlug}
                      selected={isSelected}
                      component="a"
                      href={`/sectors/industry/${industrySlug}`}
                      sx={{
                        minHeight: 38,
                        px: 1.5,
                        py: 0.4,
                        borderLeft: isSelected
                          ? '3px solid'
                          : '3px solid transparent',
                        borderLeftColor: isSelected
                          ? 'primary.main'
                          : 'transparent',
                        backgroundColor: isSelected
                          ? 'rgba(22,27,87,0.04)'
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(22,27,87,0.03)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(22,27,87,0.04)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(22,27,87,0.06)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={ind?.shortName}
                        primaryTypographyProps={{
                          fontSize: '0.82rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'text.primary' : 'rgb(67,65,102)',
                          noWrap: true,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </List>
    </Grid>
  );
};

export default IndustriesList;
